import React, { FunctionComponent, useEffect, useMemo, useState } from 'react'
import {
  Chip,
  DatePicker,
  EntityCard,
  HorizontalScroller,
  IconChip,
  FormHeading,
} from '../../../../shared'

import { durationTimeUnits, timeUnitLabels } from '../PosologyDetail.model'
import {
  DurationBlockProps,
  PREDEFINED_MAX_TIME_VALUE,
  PREDEFINED_MIN_TIME_VALUE,
} from './DurationBlock.model'
import { integerRegex } from '../../../../../misc/regex'
import styles from './DurationBlock.module.scss'

export const DurationBlock: FunctionComponent<DurationBlockProps> = ({
  posologyForm,
  disabled,
  setPosologyForm,
}) => {
  const [durationSelected, setDurationSelected] = useState(true)
  const [customTimeValue, setCustomTimeValue] = useState<string>()

  useEffect(() => {
    if (posologyForm.startDate && posologyForm.endDate && !posologyForm.intakeDurationValue) {
      setDurationSelected(false)
    }
  }, [posologyForm.startDate, posologyForm.endDate, posologyForm.intakeDurationValue])

  useEffect(() => {
    if (
      posologyForm.intakeDurationValue &&
      posologyForm.intakeDurationValue > PREDEFINED_MAX_TIME_VALUE
    ) {
      setCustomTimeValue(`${posologyForm.intakeDurationValue}`)
    }
  }, [posologyForm.intakeDurationValue])

  const setDurationType = (type: string) => {
    setPosologyForm({
      ...posologyForm,
      intakeDurationType: type,
      startDate: null,
      endDate: null,
    })
  }

  const setDurationValue = (value: number, isCustomValue: boolean) => {
    setPosologyForm({
      ...posologyForm,
      intakeDurationValue: value,
      startDate: null,
      endDate: null,
    })
    if (!isCustomValue) {
      setCustomTimeValue(undefined)
    }
  }
  const testId = useMemo(
    () =>
      customTimeValue
        ? `icon_chip-posology-time_unit-${customTimeValue}select`
        : 'icon_chip-posology-time_unit-custom-setup',
    [customTimeValue],
  )

  const labelQuantity = posologyForm?.intakeDurationType === "box" ? "Quantité" : "Temps";
  return (
    <EntityCard loading={false} inactive={false}>
      <div className={styles.durationBlockContainer}>
        <FormHeading
          disabled={disabled}
          headings={[
            {
              icon: 'history',
              label: 'Durée',
              selected: durationSelected,
              onSelect: () => setDurationSelected(true),
            },
            {
              icon: 'calendar',
              label: 'Début/Fin',
              selected: !durationSelected,
              onSelect: () => setDurationSelected(false),
            },
          ]}
        />
        {durationSelected && (
          <>
            <div className={styles.durationBlockSubPart}>
              <HorizontalScroller
                renderTitle={() => <span className={styles.durationBlockMomentTitle}>{labelQuantity}</span>}
              >
                <div className={styles.durationBlockMomentOptions}>
                  {Array.from(Array(PREDEFINED_MAX_TIME_VALUE - PREDEFINED_MIN_TIME_VALUE + 1)).map(
                    (_, index) => {
                      const timeItem = index + PREDEFINED_MIN_TIME_VALUE
                      return (
                        <Chip
                          colorPreset="dark"
                          label={`${timeItem}`}
                          key={timeItem}
                          onClick={() => setDurationValue(timeItem, false)}
                          selected={
                            timeItem === posologyForm.intakeDurationValue &&
                            customTimeValue === undefined
                          }
                          renderLabel={(label) => <span>{label}</span>}
                          disabled={disabled}
                          testId={`chip-posology_duration_time-${timeItem}-select`}
                        />
                      )
                    },
                  )}
                  <IconChip
                    selected={customTimeValue !== undefined}
                    editMode={customTimeValue !== undefined}
                    onClick={() => setCustomTimeValue('')}
                    label={customTimeValue !== undefined ? customTimeValue : 'Autre'}
                    onTitleEdit={(title) => {
                      setCustomTimeValue(title)
                      if (integerRegex.test(title)) {
                        setDurationValue(parseInt(title), true)
                      }
                    }}
                    icon="text"
                    disabled={disabled}
                    testId={testId}
                  />
                </div>
              </HorizontalScroller>
            </div>
            <div className={styles.durationBlockSubPart}>
              <HorizontalScroller
                renderTitle={() => (
                  <span className={styles.durationBlockMomentTitle}>Unité</span>
                )}
              >
                <div className={styles.durationBlockMomentOptions}>
                  {durationTimeUnits.map((unit) => (
                    <Chip
                      colorPreset="dark"
                      label={timeUnitLabels[unit] ?? unit}
                      key={unit}
                      selected={unit === posologyForm.intakeDurationType}
                      onClick={() => setDurationType(unit)}
                      renderLabel={(label) => <span>{label}</span>}
                      disabled={disabled}
                      testId={`posology-duration-unit-${unit}`}
                    />
                  ))}
                </div>
              </HorizontalScroller>
            </div>
          </>
        )}
        {!durationSelected && (
          <>
            <div className={styles.durationBlockSubPart}>
              <DatePicker
                label="Date de début"
                value={posologyForm.startDate}
                colorPreset="light"
                onChange={(value) => {
                  setPosologyForm({
                    ...posologyForm,
                    startDate: value,
                    intakeDurationType: undefined,
                    intakeDurationValue: undefined,
                  })
                }}
                disabled={disabled}
                testId="date_picker-drug-posology_duration-start"
              />
            </div>
            <div className={styles.durationBlockSubPart}>
              <DatePicker
                label="Date de fin"
                value={posologyForm.endDate}
                colorPreset="light"
                onChange={(value) => {
                  setPosologyForm({
                    ...posologyForm,
                    endDate: value,
                    intakeDurationType: undefined,
                    intakeDurationValue: undefined,
                  })
                }}
                disabled={disabled}
                testId="date_picker-drug-posology_duration-end"
              />
            </div>
          </>
        )}
      </div>
    </EntityCard>
  )
}
